// Owns the canvas drag/drop event surface: what happens when a sidebar
// template, an existing flow file, or a future-connector target is dropped
// onto the canvas. The cross-cutting helpers (`findTemplateById`,
// `buildNodesForTemplateDrop`, etc.) stay in WorkflowCanvas because they're
// also used by the keyboard "add template to center" path and the render
// block. This hook only owns the drop-event handlers themselves.

const { cloneTemplateNode, getFutureNodePosition, autoSetVariableSourceForTarget } = require('../lib/canvasUtils');
const { loadWorkflowStateById } = require('../lib/workflowStorage');
const { createFlowRefTemplate } = require('../data/workflowData');
const dragData = require('../lib/dragData');

function useCanvasDrop(deps) {
  // refs
  var panPosRef = deps.panPosRef;
  var zoomRef = deps.zoomRef;

  // state setters
  var setWorkflowState = deps.setWorkflowState;
  var setDropActive = deps.setDropActive;
  var setFutureDropTargetId = deps.setFutureDropTargetId;
  var setSelectedNodeIds = deps.setSelectedNodeIds;
  var setSelectedConnectionId = deps.setSelectedConnectionId;
  var setPendingAgentDrop = deps.setPendingAgentDrop;
  var setPendingClassifierDrop = deps.setPendingClassifierDrop;

  // values
  var dropActive = deps.dropActive;
  var workflowId = deps.workflowId;

  // helpers borrowed from WorkflowCanvas
  var findTemplateById = deps.findTemplateById;
  var buildNodesForTemplateDrop = deps.buildNodesForTemplateDrop;
  var addVariableNode = deps.addVariableNode;
  var autoRunNewNode = deps.autoRunNewNode;
  var showAlert = deps.showAlert;

  function onCanvasDragOver(event) {
    var hasTemplateType = dragData.hasType(event.dataTransfer, dragData.TEMPLATE_TYPE);
    var hasFlowType = dragData.hasType(event.dataTransfer, dragData.FLOW_TYPE);
    if (!hasTemplateType && !hasFlowType) return;
    event.preventDefault();
    try { event.dataTransfer.dropEffect = 'copy'; } catch (err) {}
    if (!dropActive) setDropActive(true);
  }

  function onCanvasDragLeave(event) {
    if (!event.currentTarget.contains(event.relatedTarget)) {
      setDropActive(false);
      setFutureDropTargetId(null);
    }
  }

  function dropTemplateOnFutureConnector(template, futureConnector, event) {
    if (!template || !futureConnector) return;
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    setDropActive(false);
    setFutureDropTargetId(null);

    if (template.icon === 'variables') {
      addVariableNode();
      return;
    }

    var newId = template.id + '-node-' + Date.now().toString(36);
    var newPos = getFutureNodePosition(template, futureConnector);
    var targetAnchor = 'left';
    if (futureConnector.anchor === 'left') targetAnchor = 'right';
    else if (futureConnector.anchor === 'top') targetAnchor = 'bottom';
    else if (futureConnector.anchor === 'bottom') targetAnchor = 'top';

    var computedNextState = null;
    setWorkflowState(function(prev) {
      var nextNodes = buildNodesForTemplateDrop(prev.nodes || [], template, newId, newPos);
      nextNodes = autoSetVariableSourceForTarget(nextNodes, newId, false);
      var next = Object.assign({}, prev, {
        nodes: nextNodes,
        connections: (prev.connections || []).concat({
          id: 'manual-conn-' + Date.now().toString(36),
          from: futureConnector.nodeId,
          to: newId,
          fromAnchor: futureConnector.anchor,
          toAnchor: targetAnchor
        })
      });
      computedNextState = next;
      return next;
    });

    var sel = {}; sel[newId] = true;
    setSelectedNodeIds(sel);
    setSelectedConnectionId(null);
    setFutureDropTargetId(null);
    autoRunNewNode(newId, computedNextState);
  }

  // Build a flow-ref node from a referenced flow's saved state. Hyperparameters
  // are taken from the referenced flow's start node so dropping a flow exposes
  // its start params as editable inputs on the resulting node. The referenced
  // flow's id and name are kept in the node so the runtime can resolve which
  // flow to invoke and the UI can display a meaningful title.
  function buildFlowRefNode(refFlowId, refFlowName, refFlowState, newId, dropPosition) {
    var template = createFlowRefTemplate();
    var startNode = null;
    var refNodes = (refFlowState && Array.isArray(refFlowState.nodes)) ? refFlowState.nodes : [];
    for (var i = 0; i < refNodes.length; i += 1) {
      if (refNodes[i] && refNodes[i].icon === 'start') { startNode = refNodes[i]; break; }
    }
    var startParams = (startNode && Array.isArray(startNode.hyperparameters))
      ? startNode.hyperparameters
      : [];
    var clonedParams = startParams.map(function(p) { return Object.assign({}, p); });
    var paramValues = clonedParams.reduce(function(acc, p) {
      if (!p || !p.key) return acc;
      // Carry over the start node's saved default for each param so the user
      // sees a sensible initial value before editing.
      var startSaved = startNode && startNode.paramValues && startNode.paramValues[p.key];
      acc[p.key] = startSaved !== undefined
        ? startSaved
        : (p.default !== undefined ? p.default : '');
      return acc;
    }, {});

    var node = cloneTemplateNode(template, newId, dropPosition);
    node.hyperparameters = clonedParams;
    node.paramValues = paramValues;
    node.title = String(refFlowName || (refFlowState && refFlowState.name) || 'Flow');
    node.flowRefId = String(refFlowId || '');
    node.flowRefName = node.title;
    return node;
  }

  function handleWorkflowFileDrop(payload, dropPosition) {
    var refFlowId = String((payload && payload.id) || '').trim();
    if (!refFlowId) return;
    // Prevent dropping a flow into itself — that would create a recursive
    // reference at runtime and is rarely what the user wants.
    if (workflowId && refFlowId === workflowId) {
      showAlert("Can't drop a flow into itself.");
      return;
    }

    var newId = 'sys-flow-ref-' + Date.now().toString(36);
    loadWorkflowStateById(refFlowId).then(function(refState) {
      var node = buildFlowRefNode(refFlowId, payload && payload.name, refState, newId, dropPosition);
      if (!node) return;
      setWorkflowState(function(prev) {
        return Object.assign({}, prev, { nodes: (prev.nodes || []).concat(node) });
      });
      var sel = {}; sel[newId] = true;
      setSelectedNodeIds(sel);
    }).catch(function() {
      showAlert('Could not load flow.');
    });
  }

  function onCanvasDrop(event) {
    event.preventDefault();
    setDropActive(false);
    setFutureDropTargetId(null);

    var bounds = event.currentTarget.getBoundingClientRect();
    var z = zoomRef.current;
    var dropPosition = {
      left: Math.round((event.clientX - bounds.left - panPosRef.current.x) / z - 110),
      top: Math.round((event.clientY - bounds.top - panPosRef.current.y) / z - 24)
    };

    // 1. Dragging an existing flow file from the workbook explorer panel.
    var flowJson = dragData.getData(event.dataTransfer, dragData.FLOW_TYPE);
    if (flowJson) {
      var payload = null;
      try { payload = JSON.parse(flowJson); } catch (err) { payload = null; }
      if (payload && payload.id) {
        handleWorkflowFileDrop(payload, dropPosition);
        return;
      }
    }

    // 2. Dragging a node template from the sidebar.
    var templateId = dragData.getData(event.dataTransfer, dragData.TEMPLATE_TYPE);
    if (!templateId) return;

    var template = findTemplateById(templateId);
    if (!template) return;

    if (template.icon === 'variables') {
      addVariableNode();
      return;
    }

    var newId = template.id + '-' + Date.now().toString(36);

    if (template.icon === 'agent') {
      setPendingAgentDrop({ template: template, dropPosition: dropPosition, newId: newId });
      return;
    }

    if (template.icon === 'classifier') {
      setPendingClassifierDrop({ template: template, dropPosition: dropPosition, newId: newId });
      return;
    }

    setWorkflowState(function(prev) {
      var nextNodes = buildNodesForTemplateDrop(prev.nodes, template, newId, dropPosition);
      return Object.assign({}, prev, { nodes: nextNodes });
    });
  }

  return {
    onCanvasDragOver: onCanvasDragOver,
    onCanvasDragLeave: onCanvasDragLeave,
    onCanvasDrop: onCanvasDrop,
    dropTemplateOnFutureConnector: dropTemplateOnFutureConnector,
    handleWorkflowFileDrop: handleWorkflowFileDrop,
    buildFlowRefNode: buildFlowRefNode
  };
}

module.exports = useCanvasDrop;
